/**
 * FikoStepCache — Cross-session STEP tesselation cache (IndexedDB)
 *
 * Aynı STEP/STP dosyası yeniden açıldığında OCCT tesselation'ı (en pahalı adım)
 * tamamen atlar. Anahtar = SHA-256(dosya içeriği) + tessellation parametreleri + sürüm.
 *
 * Tasarım:
 * - Tamamen additive: cache miss veya herhangi bir hata → çağıran normal OCCT yoluna döner.
 * - Yalnız OCCT mesh çıktısının lite kopyası saklanır (position/normal/index/brep_faces/color/name).
 *   Topoloji (stepTopology) ve faceColorMap çağıran tarafta dosya metninden hızlıca (WASM) yeniden üretilir.
 * - LRU benzeri budget tabanlı temizlik (toplam byte üzerinden).
 */

const DB_ADI = 'fiko-step-cache';
const STORE = 'tesselation';
const CACHE_VERSION = 1;
const BUDGET_BYTE = 350 * 1024 * 1024; // ~350 MB

export interface IFikoCacheMesh {
    position: Float32Array;
    normal?: Float32Array;
    index?: Uint32Array;
    brep_faces?: any[];
    color?: number[];
    name?: string;
}

interface IFikoCacheKayit {
    anahtar: string;
    v: number;
    olusturma: number;
    sonErisim: number;
    byte: number;
    meshler: IFikoCacheMesh[];
}

/** OCCT ReadStepFile çıktısına benzer hafif sonuç (çağıran döngü bunu okur) */
export interface IFikoOcctBenzeriSonuc {
    success: true;
    root: undefined;
    meshes: any[];
}

export class FikoStepCache {
    private static _db: IDBDatabase | null = null;
    private static _acmaSozu: Promise<IDBDatabase | null> | null = null;

    /** Cache'i devre dışı bırakmak için */
    public static aktif = true;

    private static destekli(): boolean {
        return this.aktif && typeof indexedDB !== 'undefined' && typeof crypto !== 'undefined' && !!crypto.subtle;
    }

    private static async db(): Promise<IDBDatabase | null> {
        if (this._db) return this._db;
        if (!this.destekli()) return null;
        if (this._acmaSozu) return this._acmaSozu;

        this._acmaSozu = new Promise<IDBDatabase | null>((cz) => {
            try {
                const istek = indexedDB.open(DB_ADI, 1);
                istek.onupgradeneeded = () => {
                    const db = istek.result;
                    if (!db.objectStoreNames.contains(STORE)) {
                        const os = db.createObjectStore(STORE, { keyPath: 'anahtar' });
                        os.createIndex('sonErisim', 'sonErisim');
                    }
                };
                istek.onsuccess = () => { this._db = istek.result; cz(this._db); };
                istek.onerror = () => cz(null);
            } catch {
                cz(null);
            }
        });
        return this._acmaSozu;
    }

    /** Dosya byte'larından + parametrelerden deterministik anahtar üretir */
    public static async anahtarUret(
        bytes: ArrayBuffer,
        linearDeflection: number,
        angularDeflection: number
    ): Promise<string | null> {
        if (!this.destekli()) return null;
        try {
            const ozet = await crypto.subtle.digest('SHA-256', bytes);
            const hex = Array.from(new Uint8Array(ozet)).map(b => b.toString(16).padStart(2, '0')).join('');
            return `${hex}|ld${linearDeflection}|ad${angularDeflection}|v${CACHE_VERSION}`;
        } catch {
            return null;
        }
    }

    /** Cache'ten OCCT-benzeri sonuç döndürür (yoksa null) */
    public static async al(anahtar: string | null): Promise<IFikoOcctBenzeriSonuc | null> {
        if (!anahtar) return null;
        const db = await this.db();
        if (!db) return null;

        const kayit = await new Promise<IFikoCacheKayit | null>((cz) => {
            try {
                const tx = db.transaction(STORE, 'readonly');
                const istek = tx.objectStore(STORE).get(anahtar);
                istek.onsuccess = () => cz(istek.result || null);
                istek.onerror = () => cz(null);
            } catch { cz(null); }
        });

        if (!kayit || kayit.v !== CACHE_VERSION || !kayit.meshler?.length) return null;

        // sonErisim güncelle (fire-and-forget, LRU için)
        this.erisimDokun(anahtar);

        return {
            success: true,
            root: undefined,
            meshes: kayit.meshler.map(m => ({
                attributes: {
                    position: { array: m.position },
                    ...(m.normal ? { normal: { array: m.normal } } : {})
                },
                index: m.index ? { array: m.index } : undefined,
                brep_faces: m.brep_faces,
                color: m.color,
                name: m.name
            }))
        };
    }

    /** OCCT sonucunu cache'e yazar (fire-and-forget, hataları yutar) */
    public static async koy(anahtar: string | null, occtResult: any): Promise<void> {
        if (!anahtar || !occtResult?.meshes?.length || !this.destekli()) return;

        // Array'leri DB açılmadan ÖNCE senkron kopyala (WASM belleği serbest kalabilir)
        let toplamByte = 0;
        const meshler: IFikoCacheMesh[] = [];
        for (const mesh of occtResult.meshes) {
            const posKaynak = mesh?.attributes?.position?.array;
            if (!posKaynak) continue;
            const position = posKaynak instanceof Float32Array ? posKaynak.slice() : new Float32Array(posKaynak);
            const normKaynak = mesh?.attributes?.normal?.array;
            const normal = normKaynak ? (normKaynak instanceof Float32Array ? normKaynak.slice() : new Float32Array(normKaynak)) : undefined;
            const idxKaynak = mesh?.index?.array;
            const index = (idxKaynak && idxKaynak.length) ? new Uint32Array(idxKaynak) : undefined;

            toplamByte += position.byteLength + (normal?.byteLength || 0) + (index?.byteLength || 0);
            meshler.push({
                position,
                normal,
                index,
                brep_faces: mesh.brep_faces ? JSON.parse(JSON.stringify(mesh.brep_faces)) : undefined,
                color: mesh.color ? Array.from(mesh.color as number[]) : undefined,
                name: mesh.name
            });
        }
        if (!meshler.length) return;

        const db = await this.db();
        if (!db) return;

        const simdi = Date.now();
        const kayit: IFikoCacheKayit = {
            anahtar, v: CACHE_VERSION, olusturma: simdi, sonErisim: simdi, byte: toplamByte, meshler
        };

        try {
            await new Promise<void>((cz) => {
                const tx = db.transaction(STORE, 'readwrite');
                tx.objectStore(STORE).put(kayit);
                tx.oncomplete = () => cz();
                tx.onerror = () => cz();
                tx.onabort = () => cz();
            });
            this.budgetUygula();
        } catch { /* yut */ }
    }

    private static erisimDokun(anahtar: string): void {
        this.db().then(db => {
            if (!db) return;
            try {
                const tx = db.transaction(STORE, 'readwrite');
                const os = tx.objectStore(STORE);
                const g = os.get(anahtar);
                g.onsuccess = () => {
                    const k = g.result as IFikoCacheKayit;
                    if (k) { k.sonErisim = Date.now(); os.put(k); }
                };
            } catch { /* yut */ }
        });
    }

    /** Toplam byte budget'ı aşılırsa en eski erişilenleri siler */
    private static async budgetUygula(): Promise<void> {
        const db = await this.db();
        if (!db) return;
        try {
            const kayitlar = await new Promise<IFikoCacheKayit[]>((cz) => {
                const tx = db.transaction(STORE, 'readonly');
                const istek = tx.objectStore(STORE).getAll();
                istek.onsuccess = () => cz(istek.result || []);
                istek.onerror = () => cz([]);
            });
            let toplam = kayitlar.reduce((t, k) => t + (k.byte || 0), 0);
            if (toplam <= BUDGET_BYTE) return;

            kayitlar.sort((a, b) => a.sonErisim - b.sonErisim); // en eski önce
            const tx = db.transaction(STORE, 'readwrite');
            const os = tx.objectStore(STORE);
            for (const k of kayitlar) {
                if (toplam <= BUDGET_BYTE) break;
                os.delete(k.anahtar);
                toplam -= (k.byte || 0);
            }
        } catch { /* yut */ }
    }

    /** Tüm cache'i temizler */
    public static async temizle(): Promise<void> {
        const db = await this.db();
        if (!db) return;
        try {
            const tx = db.transaction(STORE, 'readwrite');
            tx.objectStore(STORE).clear();
        } catch { /* yut */ }
    }
}
